import React, { useCallback, useMemo } from 'react'

import {
  ContextMenu,
  ContextMenuPlacements,
  ContextMenuTriggerOnActions
} from '@inno-ui/context-menu'
import { ContextMenuOptionType } from '@inno-ui/context-menu'
import {
  ArrowUpRectangleLineIcon,
  ChainLinkIcon,
  EyeLineIcon,
  EyeSlashLineIcon,
  MultiplyIcon,
  PinLineIcon,
  PinSlashLineIcon,
  ScreenLineIcon
} from '@inno-ui/icons'
import { toast } from '@inno-ui/toast'
import { useCreateShortcutMutation } from 'modules/desktop/modules/files-integration/api'
import {
  useDesktopStore,
  useWindowStore
} from 'modules/desktop/pages/MainPage/stores'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { usePinAppMutation, useUnpinAppMutation } from 'shared/api/containerApi'
import { APP_ROUTES } from 'shared/constants/routes'
import { useCommonStore } from 'shared/stores/commonStore'
import { useLaunchpadStore } from 'shared/stores/launchpadStore'
import { useTopDockStore } from 'shared/stores/topDockStore'

import { useAppButtonState } from '../../hooks/useAppButtonState'
import { AppButtonWrapperProps } from './AppButtonWrapper.types'

type OptionWithButtonTypes = ContextMenuOptionType & {
  buttonTypes: ('launchpad' | 'topDock')[]
  showOnLockedApp?: boolean
}

export const AppButtonWrapper: React.FC<AppButtonWrapperProps> = ({
  children,
  app,
  buttonType,
  contextMenuRef,
  iconRefs,
  dataTour,
  dataAppButtonWrapper,
  isLocked,
  handleOpenWindow
}) => {
  const appId = app.id
  const { t } = useTranslation('translation', {
    keyPrefix: 'apps_context_menu'
  })
  const navigate = useNavigate()

  const buttonState = useAppButtonState(app)
  const closeWindow = useWindowStore(state => state.closeWindow)
  const pinnedAppIds = useTopDockStore(state => state.pinnedAppIds)
  const addPinnedAppId = useTopDockStore(state => state.addPinnedAppId)
  const removePinnedAppId = useTopDockStore(state => state.removePinnedAppId)
  const isDetached = useCommonStore(state => state.isDetached)
  const setIsLaunchpadOpen = useLaunchpadStore(
    state => state.setIsLaunchpadOpen
  )
  const isRichAppIconDisabled = useTopDockStore(state =>
    state.isRichAppIconDisabled(appId)
  )
  const toggleRichAppIcon = useTopDockStore(state => state.toggleRichAppIcon)
  const richAppIconData = useTopDockStore(state =>
    state.getRichAppIconData(appId)
  )
  const pinAppMutation = usePinAppMutation()
  const unpinAppMutation = useUnpinAppMutation()
  const createShortcutMutation = useCreateShortcutMutation()
  const highlightShortcut = useDesktopStore(state => state.highlightShortcut)

  const { hasOpenWindow, isExpanded, currentAppWindow } = buttonState
  const isPinned = useMemo(
    () => pinnedAppIds.includes(appId),
    [pinnedAppIds, appId]
  )
  const isOpen = buttonType === 'launchpad' ? hasOpenWindow : isExpanded

  const hasRichAppIcon = Boolean(app.richAppIcon)

  const handleMenuAction = useCallback(() => {
    if (isPinned) {
      removePinnedAppId(appId)
      unpinAppMutation.mutate(appId)
    } else {
      addPinnedAppId(appId)
      pinAppMutation.mutate(appId)
    }
  }, [
    isPinned,
    appId,
    addPinnedAppId,
    removePinnedAppId,
    pinAppMutation,
    unpinAppMutation
  ])

  const handleOpenInNewTab = useCallback(() => {
    window.open(app.url ?? '', '_blank', 'noopener,noreferrer')
  }, [app.url])

  const handleCreateShortcut = useCallback(() => {
    if (!app.url) {
      toast.error({ title: t('shortcut_creation.failed') })
      return
    }

    const errorState: { title: string; subTitle?: string } = {
      title: t('shortcut_creation.failed')
    }
    const promise = createShortcutMutation
      .mutateAsync({
        name: app.name,
        shortcutUrl: new URL(app.url, window.location.origin).toString()
      })
      .then(shortcut => {
        setIsLaunchpadOpen(false)
        highlightShortcut(shortcut.id)
        return shortcut
      })
      .catch(error => {
        if (error instanceof Error && error.message) {
          errorState.subTitle = error.message
        }
        throw error
      })

    toast.info({
      asyncHandler: promise,
      asyncStates: {
        loading: { title: t('shortcut_creation.loading') },
        success: { title: t('shortcut_creation.success') },
        error: errorState
      }
    })
  }, [
    app.name,
    app.url,
    createShortcutMutation,
    highlightShortcut,
    setIsLaunchpadOpen,
    t
  ])

  const handleCloseWindow = useCallback(() => {
    setIsLaunchpadOpen(false)
    if (isDetached) {
      navigate(APP_ROUTES.desktop.path)
    } else {
      closeWindow(appId)
      currentAppWindow?.onCloseCallback?.()
    }
  }, [
    closeWindow,
    appId,
    currentAppWindow,
    setIsLaunchpadOpen,
    isDetached,
    navigate
  ])

  const menuOptions = useMemo<ContextMenuOptionType[]>(() => {
    const baseButtonTypes: ('launchpad' | 'topDock')[] = [
      'topDock',
      'launchpad'
    ]
    const options: (OptionWithButtonTypes | false)[] = [
      {
        showOnLockedApp: true,
        icon: isPinned ? PinSlashLineIcon : PinLineIcon,
        label: isPinned ? t('unpin_from_dock') : t('pin_to_dock'),
        'data-id': 'pin-to-dock',
        onClick: handleMenuAction,
        buttonTypes: baseButtonTypes
      },
      {
        icon: ChainLinkIcon,
        label: t('add_shortcut_to_desktop'),
        'data-id': 'add-shortcut-to-desktop',
        onClick: handleCreateShortcut,
        buttonTypes: ['launchpad']
      },
      !isOpen && {
        icon: ScreenLineIcon,
        label: t('open_in_window'),
        onClick: handleOpenWindow,
        buttonTypes: baseButtonTypes
      },
      !isOpen && {
        icon: ArrowUpRectangleLineIcon,
        label: t('open_in_new_tab'),
        onClick: handleOpenInNewTab,
        buttonTypes: baseButtonTypes
      },
      isOpen && {
        icon: MultiplyIcon,
        label: t('close_window'),
        onClick: handleCloseWindow,
        buttonTypes: baseButtonTypes
      },
      hasRichAppIcon && {
        icon: isRichAppIconDisabled ? EyeLineIcon : EyeSlashLineIcon,
        label: isRichAppIconDisabled
          ? t('enable_rich_app_icon')
          : t('disable_rich_app_icon'),
        onClick: () => toggleRichAppIcon(appId),
        buttonTypes: ['topDock']
      }
    ]
    const baseOptions = options
      .filter((opt): opt is OptionWithButtonTypes => Boolean(opt))
      .filter(opt => {
        if (!opt.buttonTypes.includes(buttonType)) {
          return false
        }
        if (buttonType === 'topDock' && isLocked) {
          return Boolean(opt.showOnLockedApp)
        }
        return true
      })
      .map(({ buttonTypes: _, ...option }) => option)

    const richContextMenuOptions = richAppIconData?.contextMenuOptions

    if (richContextMenuOptions?.length && buttonType === 'topDock') {
      return [...baseOptions, { isSeparator: true }, ...richContextMenuOptions]
    }

    return baseOptions
  }, [
    isPinned,
    t,
    handleMenuAction,
    handleCreateShortcut,
    isOpen,
    handleOpenWindow,
    handleOpenInNewTab,
    handleCloseWindow,
    hasRichAppIcon,
    isRichAppIconDisabled,
    richAppIconData?.contextMenuOptions,
    buttonType,
    toggleRichAppIcon,
    appId,
    isLocked
  ])

  const triggerNode = (
    <div
      data-tour={dataTour}
      {...(dataAppButtonWrapper && { 'data-app-button-wrapper': true })}
      ref={el => {
        if (iconRefs?.current && el) {
          iconRefs.current[String(app.id)] = el
        }
      }}
    >
      {children}
    </div>
  )
  if (isLocked && buttonType === 'launchpad') {
    return triggerNode
  }

  return (
    <ContextMenu
      ref={contextMenuRef}
      triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
      zIndex={1101}
      placement={ContextMenuPlacements.Bottom}
      options={menuOptions}
      triggerNode={triggerNode}
    />
  )
}
